// src/pages/dashboard/receptionist-settings/tasksScheduleSettings.tsx
import React, { useState, useEffect } from 'react';
import { Controller, Control, FieldErrors, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { 
    Loader2, CalendarCheck, PhoneOff, RotateCcw, Briefcase, Clock, Settings as SettingsIcon, 
    ChevronDown, ChevronUp, Sparkles, Trophy, CheckCircle 
} from 'lucide-react'; // Removed BadgeDollarSign
import InfoModal from '@/components/ui/InfoModal';
import { SettingsFormData } from './settingsTypes';
import { daysOfWeek, commonTimezones } from './settingsConstants';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { useVideoBySlug } from '@/hooks/useVideos';

interface TasksScheduleSettingsProps {
    control: Control<SettingsFormData>;
    errors: FieldErrors<SettingsFormData>;
    watch: UseFormWatch<SettingsFormData>;
    setValue: UseFormSetValue<SettingsFormData>;
    isSettingsDirty: boolean;
    isSaving: boolean;
    isSettingsFetching: boolean;
}

type TaskTimingPresetUnit = "minutes" | "hours" | "days";

type TaskTimingPreset = {
    label: string;
    value: number;
    unit: TaskTimingPresetUnit;
};

const taskPresets = {
    appointments: [
        { label: "Day before (24h)", value: 24, unit: "hours" },
        { label: "Morning of (4h)", value: 4, unit: "hours" },
        { label: "2 hours before", value: 2, unit: "hours" },
        { label: "1 hour before", value: 1, unit: "hours" },
    ] as TaskTimingPreset[],
    cancellations: [
        { label: "Immediately (15m)", value: 15, unit: "minutes" },
        { label: "1 hour after", value: 1, unit: "hours" },
        { label: "Next day (24h)", value: 24, unit: "hours" },
    ] as TaskTimingPreset[],
    noShows: [
        { label: "15 mins after", value: 15, unit: "minutes" },
        { label: "30 mins after", value: 30, unit: "minutes" },
        { label: "1 hour after", value: 1, unit: "hours" },
    ] as TaskTimingPreset[],
};

interface TaskCardProps {
    icon: React.ReactNode;
    title: string;
    valueProposition: string; 
    description: string; 
    enabled: boolean | undefined;
    onToggle: (enabled: boolean) => void;
    timingValuePath: `call_activation_settings.${keyof SettingsFormData['call_activation_settings']}.timing_value`;
    timingUnitPath: `call_activation_settings.${keyof SettingsFormData['call_activation_settings']}.timing_unit`;
    presets: TaskTimingPreset[];
    taskKey: keyof SettingsFormData['call_activation_settings']; 
    control: Control<SettingsFormData>;
    errors: FieldErrors<SettingsFormData>;
    watch: UseFormWatch<SettingsFormData>; // Watch needed to get current values for custom display
    setValue: UseFormSetValue<SettingsFormData>; // setValue needed to update form
    helperTextPrefix: string; 
    helperTextSuffix: string; 
}

const TaskCard: React.FC<TaskCardProps> = ({
    icon, title, valueProposition, description, enabled, onToggle,
    timingValuePath, timingUnitPath, presets, taskKey,
    control, errors, watch, setValue, helperTextPrefix, helperTextSuffix
}) => {
    
    const currentTimingValue = watch(timingValuePath);
    const currentTimingUnit = watch(timingUnitPath);
    const [isCustomTiming, setIsCustomTiming] = useState(false);

    const currentPreset = presets.find(p => p.value === currentTimingValue && p.unit === currentTimingUnit);

    useEffect(() => {
        // If current values don't match any preset, consider it custom
        if (enabled && !currentPreset && currentTimingValue !== undefined && currentTimingUnit !== undefined) {
            setIsCustomTiming(true);
        } else if (currentPreset) {
            setIsCustomTiming(false);
        }
    }, [currentTimingValue, currentTimingUnit, currentPreset, enabled]);


    const handlePresetOrCustomChange = (value: string) => {
        if (value === "custom") {
            setIsCustomTiming(true);
            // Optionally, set to a default custom value if current ones are undefined
            if (currentTimingValue === undefined && presets.length > 0) {
                setValue(timingValuePath, presets[0].value, { shouldDirty: true });
                setValue(timingUnitPath, presets[0].unit, { shouldDirty: true });
            }
        } else {
            const selectedPreset = presets.find(p => p.label === value);
            if (selectedPreset) {
                setValue(timingValuePath, selectedPreset.value, { shouldDirty: true });
                setValue(timingUnitPath, selectedPreset.unit, { shouldDirty: true });
                setIsCustomTiming(false);
            }
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: "spring", stiffness: 100 }}
            className={cn(
                "p-6 rounded-xl shadow-lg border transition-all duration-300",
                enabled ? "bg-white border-green-300 hover:shadow-green-100" : "bg-slate-50 border-slate-200 hover:shadow-slate-100"
            )}
        >
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                    <div className={cn("p-3 rounded-full", enabled ? "bg-green-100" : "bg-slate-200")}>
                        {React.cloneElement(icon as React.ReactElement, { className: cn("h-7 w-7", enabled ? "text-green-600" : "text-slate-500") })}
                    </div>
                    <div>
                        <h4 className="text-xl font-semibold text-slate-800">{title}</h4>
                        <p className={cn("text-sm font-medium", enabled ? "text-green-600" : "text-slate-500")}>{valueProposition}</p>
                    </div>
                </div>
                <motion.div whileTap={{ scale: 0.95 }}>
                    <Switch checked={!!enabled} onCheckedChange={onToggle} aria-label={`Enable ${title}`} />
                </motion.div>
            </div>
            <AnimatePresence>
            {enabled && (
                <motion.div 
                    initial={{ height: 0, opacity: 0, marginTop: 0 }}
                    animate={{ height: 'auto', opacity: 1, marginTop: '1rem' }}
                    exit={{ height: 0, opacity: 0, marginTop: 0 }}
                    className="overflow-hidden"
                >
                    <p className="text-sm text-slate-600 mb-3">{description}</p>
                    <div className="space-y-3 bg-slate-100 p-4 rounded-md">
                        <div className="flex flex-col sm:flex-row items-center gap-2">
                            <span className="text-sm text-slate-700 whitespace-nowrap">{helperTextPrefix}</span>
                            <Select
                                onValueChange={handlePresetOrCustomChange}
                                value={isCustomTiming ? "custom" : currentPreset?.label || ""}
                            >
                                <SelectTrigger className="w-full sm:w-[200px] bg-white">
                                    <SelectValue placeholder="Select timing..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {presets.map(p => <SelectItem key={p.label} value={p.label}>{p.label}</SelectItem>)}
                                    <SelectItem value="custom">Custom...</SelectItem>
                                </SelectContent>
                            </Select>
                             <span className="text-sm text-slate-700 whitespace-nowrap">{helperTextSuffix}</span>
                        </div>
                        
                        {isCustomTiming && (
                             <motion.div 
                                initial={{ opacity: 0, y: -10 }} 
                                animate={{ opacity: 1, y: 0 }}
                                className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 pl-0 sm:pl-4 mt-2"
                            >
                                <Controller
                                    name={timingValuePath}
                                    control={control}
                                    render={({ field }) => <Input {...field} type="number" min="1" className="w-full sm:w-24 bg-white" />}
                                />
                                <Controller
                                    name={timingUnitPath}
                                    control={control}
                                    render={({ field }) => (
                                        <Select onValueChange={field.onChange} value={field.value ?? 'hours'}>
                                            <SelectTrigger className="w-full sm:w-[120px] bg-white">
                                                <SelectValue placeholder="Unit" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="minutes">Minutes</SelectItem>
                                                <SelectItem value="hours">Hours</SelectItem>
                                                <SelectItem value="days">Days</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    )}
                                />
                            </motion.div>
                        )}
                    </div>
                    {(errors.call_activation_settings?.[taskKey]?.timing_value || errors.call_activation_settings?.[taskKey]?.timing_unit) && (
                        <div className="w-full text-red-600 text-sm mt-2">
                            {errors.call_activation_settings?.[taskKey]?.timing_value?.message || errors.call_activation_settings?.[taskKey]?.timing_unit?.message || "Invalid timing setting"}
                        </div>
                    )}
                </motion.div>
            )}
            </AnimatePresence>
        </motion.div>
    );
};


export default function TasksScheduleSettings({
    control,
    errors,
    watch,
    setValue, 
    isSettingsDirty,
    isSaving,
    isSettingsFetching
}: TasksScheduleSettingsProps) {
    const followUpsVideo = useVideoBySlug('automated-follow-ups');
    const aiCallingHoursVideo = useVideoBySlug('ai-calling-hours');
    const persistenceSettingsVideo = useVideoBySlug('persistence-settings');
    const callActivationSettings = watch('call_activation_settings');
    const aiCallingHours = watch('ai_calling_hours');
    const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
    // const [callingHoursPreset, setCallingHoursPreset] = useState<string>("custom"); // Removed schedule presets

    const allRevenueTasksEnabled = 
        callActivationSettings?.confirm_appointments?.enabled &&
        callActivationSettings?.recover_cancellations?.enabled &&
        callActivationSettings?.reschedule_noshows?.enabled;


    useEffect(() => {
        if (!isSettingsFetching && !isSettingsDirty && !callActivationSettings?.confirm_appointments?.enabled) { 
            // Placeholder for smart defaults/industry templates
        }
    }, [isSettingsFetching, isSettingsDirty, callActivationSettings, setValue]);


    return (
        <Card className="shadow-lg">
            <CardHeader>
                <CardTitle className="flex items-center text-2xl">
                     <Briefcase className="mr-3 h-7 w-7 text-indigo-600" /> AI Workload & Hours
                </CardTitle>
                <CardDescription>Tell your AI what follow-ups to handle automatically and when it should work.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-12">
                
                <section>
                    <div className="flex items-center mb-3">
                        <Sparkles className="h-7 w-7 text-green-500 mr-3" />
                        <h3 className="text-xl font-semibold text-slate-800">Automated Follow-Ups</h3>
                        <InfoModal
                            title="Automated Follow-Ups" 
                            link="automated-follow-ups"
                            description={
                                <div className="space-y-4">
                                    <p className="text-sm text-gray-700 mb-4">
                                        Your AI can automatically make follow-up calls based on calendar events. These features require a calendar integration to work.
                                    </p>
                                    
                                    <div className="space-y-3">
                                        <div>
                                            <h4 className="font-medium mb-1">Key Features:</h4>
                                            <ul className="text-sm space-y-1 text-gray-600">
                                                <li><strong>Appointment Reminders:</strong> Reduce no-shows by confirming upcoming appointments</li>
                                                <li><strong>Cancellation Recovery:</strong> Win back cancelled appointments with follow-up calls</li>
                                                <li><strong>No-Show Recovery:</strong> Turn missed appointments into future bookings</li>
                                            </ul>
                                        </div>
                                        
                                        <div>
                                            <h4 className="font-medium mb-1">Requirements:</h4>
                                            <ul className="text-sm space-y-1 text-gray-600">
                                                <li>Calendar integration (Google Calendar, Calendly, etc.)</li>
                                                <li>Phone numbers in calendar events</li>
                                                <li>Configure timing for each follow-up type</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            }
                            isVideo={followUpsVideo !== null}
                            videoUrl={followUpsVideo || "https://www.loom.com/embed/7f712e9da3dc4598afb4e93d62791161?sid=2737547f-1b2c-45d0-8e57-1cfe4606bdee"}
                            triggerClassName="ml-2"
                        />
                    </div>
                     <p className="text-sm text-slate-600 mb-6">Your AI makes these calls so you don't have to. Activate these tasks to boost your revenue.</p>

                    {allRevenueTasksEnabled && (
                        <motion.div 
                            initial={{ opacity: 0, y: -10}} 
                            animate={{ opacity: 1, y: 0}}
                            className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 text-green-700 rounded-md shadow"
                        >
                            <div className="flex items-center">
                                <Trophy className="h-6 w-6 mr-3 text-green-600"/>
                                <div>
                                    <h4 className="font-bold">Full Revenue Protection Mode Active!</h4>
                                    <p className="text-sm">Your AI is now working to maximize your bookings and minimize losses.</p>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    <div className="space-y-6">
                        <TaskCard
                            icon={<CalendarCheck />}
                            title="Appointment Reminders"
                            valueProposition="Reduce no-shows by up to 40%"
                            description={`Your AI, ${watch('ai_name') || 'Jade'}, calls to confirm if clients are still coming.`}
                            enabled={callActivationSettings?.confirm_appointments?.enabled}
                            onToggle={(e) => setValue('call_activation_settings.confirm_appointments.enabled', e, { shouldDirty: true })}
                            timingValuePath="call_activation_settings.confirm_appointments.timing_value"
                            timingUnitPath="call_activation_settings.confirm_appointments.timing_unit"
                            presets={taskPresets.appointments}
                            taskKey="confirm_appointments"
                            control={control} errors={errors} watch={watch} setValue={setValue}
                            helperTextPrefix="Remind customers"
                            helperTextSuffix="before their appointment"
                        />
                        <TaskCard
                            icon={<PhoneOff />}
                            title="Win Back Cancellations"
                            valueProposition="70% of cancellations can be saved"
                            description={`Your AI, ${watch('ai_name') || 'Jade'}, calls clients after they cancel to offer rescheduling.`}
                            enabled={callActivationSettings?.recover_cancellations?.enabled}
                            onToggle={(e) => setValue('call_activation_settings.recover_cancellations.enabled', e, { shouldDirty: true })}
                            timingValuePath="call_activation_settings.recover_cancellations.timing_value"
                            timingUnitPath="call_activation_settings.recover_cancellations.timing_unit"
                            presets={taskPresets.cancellations}
                            taskKey="recover_cancellations"
                            control={control} errors={errors} watch={watch} setValue={setValue}
                            helperTextPrefix="Call"
                            helperTextSuffix="after they cancel"
                        />
                        <TaskCard
                            icon={<RotateCcw />}
                            title="Recover Lost Revenue (No-Shows)"
                            valueProposition="Turn no-shows into future bookings"
                            description={`Your AI, ${watch('ai_name') || 'Jade'}, follows up with no-shows to get them back on the calendar.`}
                            enabled={callActivationSettings?.reschedule_noshows?.enabled}
                            onToggle={(e) => setValue('call_activation_settings.reschedule_noshows.enabled', e, { shouldDirty: true })}
                            timingValuePath="call_activation_settings.reschedule_noshows.timing_value"
                            timingUnitPath="call_activation_settings.reschedule_noshows.timing_unit"
                            presets={taskPresets.noShows}
                            taskKey="reschedule_noshows"
                            control={control} errors={errors} watch={watch} setValue={setValue}
                            helperTextPrefix="Call"
                            helperTextSuffix="after they miss their appointment"
                        />
                    </div>
                     <div className="mt-6 p-4 border rounded-md bg-slate-50/70 flex items-center gap-3">
                        <Briefcase className="h-6 w-6 text-slate-500 flex-shrink-0" />
                        <div>
                            <Label className="font-semibold text-slate-700">Always Ready for New Bookings</Label>
                            <p className="text-xs text-slate-500">Your AI answers calls 24/7 and books appointments directly. This is always active.</p>
                        </div>
                        <Switch checked={true} disabled aria-label="Book Inbound Calls always enabled" className="ml-auto"/>
                    </div>
                </section>

                <section className="pt-10 border-t border-slate-200">
                    <div className="flex items-center mb-3">
                        <Clock className="h-7 w-7 text-blue-500 mr-3" />
                        <h3 className="text-xl font-semibold text-slate-800">When Your AI Makes Calls</h3>
                         <InfoModal 
                             title="AI Calling Hours" 
                             link="ai-calling-hours"
                             description={
                                 <div className="space-y-4">
                                     <p className="text-sm text-gray-700 mb-4">
                                         Define when your AI can make outbound calls. Inbound calls are always answered 24/7, but this schedule controls proactive calls like confirmations and follow-ups.
                                     </p>
                                     
                                     <div className="space-y-3">
                                         <div>
                                             <h4 className="font-medium mb-1">What This Controls:</h4>
                                             <ul className="text-sm space-y-1 text-gray-600">
                                                 <li><strong>Outbound Calls:</strong> Appointment confirmations, cancellation recovery, no-show follow-ups</li>
                                                 <li><strong>Manual Calls:</strong> Calls you initiate through the Call Queue</li>
                                                 <li><strong>Automated Calls:</strong> Calls triggered by calendar events</li>
                                             </ul>
                                         </div>
                                         
                                         <div>
                                             <h4 className="font-medium mb-1">What's Always Active:</h4>
                                             <ul className="text-sm space-y-1 text-gray-600">
                                                 <li><strong>Inbound Calls:</strong> Customers can call your AI anytime</li>
                                                 <li><strong>Appointment Booking:</strong> AI answers and books 24/7</li>
                                             </ul>
                                         </div>
                                     </div>
                                 </div>
                             }
                             isVideo={aiCallingHoursVideo !== null}
                             videoUrl={aiCallingHoursVideo || "https://www.loom.com/embed/b33ec3e2576c4790adf46c774784cad8?sid=cce12e48-b492-4d86-b351-5fc1489ea933"}
                             triggerClassName="ml-2"
                         />
                    </div>
                    <p className="text-sm text-slate-600 mb-6">Set respectful hours for outbound calls (inbound always works).</p>
                    
                    <div className="mb-6 space-y-3">
                        <Label htmlFor="timezone-select">Calling Hours Timezone</Label>
                        <Controller 
                            name="ai_calling_hours.timezone" 
                            control={control} 
                            render={({ field }) => ( 
                                <Select 
                                    onValueChange={field.onChange} 
                                    value={field.value ?? commonTimezones[3]}
                                >
                                    <SelectTrigger id="timezone-select" className="w-full sm:w-[300px]"><SelectValue placeholder="Select timezone..." /></SelectTrigger>
                                    <SelectContent>{commonTimezones.map(tz => (<SelectItem key={tz} value={tz}>{tz.replace(/_/g, ' ')}</SelectItem>))}</SelectContent>
                                </Select> 
                            )} 
                        />
                        {errors.ai_calling_hours?.timezone && <p className="text-red-600 text-sm mt-1">{errors.ai_calling_hours.timezone.message}</p>}
                        <p className="text-xs text-slate-500">Current detected browser timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone.replace(/_/g, ' ')}</p>
                    </div>
                    
                    {/* Removed Schedule Presets Buttons */}
                    
                    <div className="space-y-4 border p-4 rounded-md bg-white shadow">
                         <p className="text-sm text-slate-500 mb-2">Fine-tune daily calling hours below:</p>
                        {daysOfWeek.map((day) => (
                            <div key={day} className="flex flex-col sm:flex-row items-center justify-between gap-3 p-3 border rounded-md bg-slate-50/50">
                                <Controller name={`ai_calling_hours.days.${day}.enabled`} control={control} render={({ field }) => ( <div className="flex items-center space-x-2 flex-shrink-0 w-full sm:w-auto justify-start sm:justify-center"><Checkbox id={`enabled-${day}`} checked={field.value ?? false} onCheckedChange={field.onChange} /><Label htmlFor={`enabled-${day}`} className="font-medium min-w-[80px]">{day}</Label></div> )}/>
                                <div className="flex items-center gap-2 flex-grow w-full sm:w-auto justify-center sm:justify-end">
                                    <Controller name={`ai_calling_hours.days.${day}.start`} control={control} render={({ field }) => (<Input type="time" className="w-full sm:w-auto" disabled={!watch(`ai_calling_hours.days.${day}.enabled`)} {...field} value={field.value ?? '09:00'} />)} />
                                    <span className="text-muted-foreground">-</span>
                                    <Controller name={`ai_calling_hours.days.${day}.end`} control={control} render={({ field }) => (<Input type="time" className="w-full sm:w-auto" disabled={!watch(`ai_calling_hours.days.${day}.enabled`)} {...field} value={field.value ?? '17:00'} />)} />
                                </div>
                                {(errors.ai_calling_hours?.days?.[day]?.start || errors.ai_calling_hours?.days?.[day]?.end) && (
                                    <div className="w-full text-right text-red-600 text-sm mt-1 sm:mt-0 sm:ml-2 flex-shrink-0" style={{minWidth: '100px'}}>
                                        {errors.ai_calling_hours?.days?.[day]?.start?.message} {errors.ai_calling_hours?.days?.[day]?.end?.message}
                                    </div>
                                )}
                            </div>
                        ))}
                        {errors.ai_calling_hours?.days?.message && (<p className="text-red-600 text-sm mt-2">{errors.ai_calling_hours.days.message}</p>)}
                        {errors.ai_calling_hours?.root?.message && !errors.ai_calling_hours?.days?.message && (<p className="text-red-600 text-sm mt-2">{errors.ai_calling_hours.root.message}</p>)}
                    </div>
                    
                     <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-700">
                        Your AI will make outbound calls in {aiCallingHours?.timezone.replace('_',' ') || 'your selected timezone'}
                    </div>
                </section>

                <section className="pt-10 border-t border-slate-200">
                     <Card className="bg-slate-50 border-slate-200 shadow-sm">
                        <CardHeader 
                            className="flex flex-row items-center justify-between cursor-pointer py-3"
                            onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                        >
                            <div className="flex items-center">
                                <SettingsIcon className="mr-3 h-6 w-6 text-slate-600" />
                                <CardTitle className="text-lg text-slate-700">Fine Tuning</CardTitle>
                            </div>
                            {showAdvancedSettings ? <ChevronUp className="h-5 w-5 text-slate-500" /> : <ChevronDown className="h-5 w-5 text-slate-500" />}
                        </CardHeader>
                        <AnimatePresence>
                        {showAdvancedSettings && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3 }}
                            >
                                <CardContent className="space-y-6 pt-4 pb-6 border-t border-slate-200">
                                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 border rounded-md gap-4 bg-white">
                                        <div className="flex items-center flex-grow whitespace-nowrap pr-4">
                                            <Label htmlFor="retry-attempts" className="font-medium">Persistence Settings</Label>
                                            <InfoModal 
                                                title="Persistence Settings" 
                                                link="persistence-settings"
                                                description={
                                                    <div className="space-y-4">
                                                        <p className="text-sm text-gray-700 mb-4">
                                                            Control how persistent your AI is when calls don't connect. This helps ensure important calls get through.
                                                        </p>
                                                        
                                                        <div className="space-y-3">
                                                            <div>
                                                                <h4 className="font-medium mb-1">How It Works:</h4>
                                                                <ul className="text-sm space-y-1 text-gray-600">
                                                                    <li><strong>Retry Attempts:</strong> Number of times to call before giving up</li>
                                                                    <li><strong>Delay Between:</strong> Time to wait between retry attempts</li>
                                                                    <li><strong>Smart Retries:</strong> Only retries if call doesn't connect (not if they hang up)</li>
                                                                </ul>
                                                            </div>
                                                            
                                                            <div>
                                                                <h4 className="font-medium mb-1">Best Practices:</h4>
                                                                <ul className="text-sm space-y-1 text-gray-600">
                                                                    <li>Start conservative (2-3 attempts, 1-2 minutes apart)</li>
                                                                    <li>Increase persistence for high-value appointments</li>
                                                                    <li>Consider your industry's calling etiquette</li>
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </div>
                                                }
                                                isVideo={persistenceSettingsVideo !== null}
                                                videoUrl={persistenceSettingsVideo || "https://www.loom.com/embed/c8bb12382efd46ff8aa497de62da9b5c?sid=5621ceec-2f38-46f1-98af-0fa8897a3725"}
                                                triggerClassName="ml-1"
                                            />
                                        </div>
                                        <div className="flex items-center space-x-3 flex-wrap flex-grow justify-end">
                                            <Controller name="call_retry_max_attempts" control={control} render={({ field }) => <Input {...field} type="number" min="1" className="w-20" />} /> <span className="text-sm text-muted-foreground">tries,</span>
                                            <Controller name="call_retry_delay_minutes" control={control} render={({ field }) => <Input {...field} type="number" min="1" className="w-24" />} /> <span className="text-sm text-muted-foreground">mins apart</span>
                                        </div>
                                        {(errors.call_retry_max_attempts || errors.call_retry_delay_minutes) && (
                                            <div className="w-full text-right mt-1 sm:mt-0">
                                                {errors.call_retry_max_attempts && <p className="text-red-600 text-sm">{errors.call_retry_max_attempts.message}</p>}
                                                {errors.call_retry_delay_minutes && <p className="text-red-600 text-sm">{errors.call_retry_delay_minutes.message}</p>}
                                            </div>
                                        )}
                                    </div>
                                    <p className="text-xs text-slate-500 px-1">Adjust how assertively your AI follows up on calls.</p>
                                </CardContent>
                            </motion.div>
                        )}
                        </AnimatePresence>
                    </Card>
                </section>


                <div className="pt-6 border-t mt-8">
                    <Button type="submit" disabled={!isSettingsDirty || isSaving} className="w-full sm:w-auto text-base px-6 py-3"> 
                        {isSaving ? (<><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Saving Workload...</>) : (<><CheckCircle className="mr-2 h-5 w-5"/>Save Workload & Hours</>)} 
                    </Button>
                    {!isSettingsDirty && !isSaving && !isSettingsFetching && (<p className="text-sm text-gray-500 mt-2 inline-block ml-4">No changes to save.</p>)}
                    {isSettingsFetching && <Loader2 className="ml-4 h-4 w-4 animate-spin inline-block text-gray-400" />}
                </div>
            </CardContent>
        </Card>
    );
}